import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// One enormous lime plane closes the page — the single largest block of colour
// in the template, ruled off from the content by a 3px black edge.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    const colHead = "eyebrow inline-block bg-foreground px-2.5 py-1.5 text-accent mb-5";
    const colLink = "text-sm font-medium text-foreground hover:bg-foreground hover:text-accent px-1 -mx-1 transition-colors duration-150";

    return (
        <footer className="mt-24 border-t-[3px] border-foreground bg-accent" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                    {/* Brand / contact */}
                    <div className="col-span-2 lg:col-span-1">
                        <span className="inline-block border-[3px] border-foreground bg-surface px-3 py-2 shadow-card">
                            <span className="font-display text-xl uppercase leading-none text-foreground">
                                {brand.site_name}
                            </span>
                        </span>
                        {brand.tagline && (
                            <p className="mt-6 text-sm font-medium leading-relaxed text-foreground/75 max-w-xs">
                                {brand.tagline}
                            </p>
                        )}
                        <p className="mt-5 font-mono text-xs uppercase tracking-wider text-foreground" data-testid="footer-contact-email">
                            {brand.email}
                        </p>
                        {brand.phone && (
                            <p className="mt-1.5 font-mono text-xs uppercase tracking-wider text-foreground/70">{brand.phone}</p>
                        )}
                    </div>

                    {/* Sections */}
                    <div>
                        <h5 className={colHead}>Sections</h5>
                        <ul className="space-y-2.5">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className={colLink}>
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h5 className={colHead}>Company</h5>
                        <ul className="space-y-2.5">
                            <li><Link to="/advertise" data-testid="footer-advertise" className={colLink}>Advertise with us</Link></li>
                            <li><Link to="/archive" className={colLink}>Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className={colLink}>Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className={colLink}>Terms of Use</Link></li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div className="border-[3px] border-foreground bg-surface p-6 shadow-card">
                        <h5 className="eyebrow inline-block bg-foreground px-2.5 py-1.5 text-accent mb-4" data-testid="footer-newsletter-title">Newsletter</h5>
                        <p className="text-sm font-medium text-dim leading-relaxed">Get the latest financial news delivered to your inbox.</p>
                        <Link
                            to="/register"
                            data-testid="footer-newsletter-subscribe"
                            className="mt-5 inline-flex items-center gap-2 border-[3px] border-foreground bg-accent px-5 py-2.5 eyebrow text-accent-foreground shadow-card hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow] duration-150"
                        >
                            Subscribe
                            <ArrowUpRight size={14} strokeWidth={3} />
                        </Link>
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="mt-5 block font-mono text-[11px] uppercase tracking-wider text-dim link-underline"
                        >
                            Unsubscribe
                        </button>
                    </div>
                </div>

                <div className="rule mt-12" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-wider text-foreground/70">
                    © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                </p>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
